// Thin Promise wrapper around the global Telegram Login Widget JS API.
//
// The widget script is loaded once from index.html (async). On click of our
// own styled TG button we call `Telegram.Login.auth(...)` which opens
// https://oauth.telegram.org/auth in a popup; on success Telegram invokes
// the callback with the user's verified identity payload.
//
// Docs: https://core.telegram.org/widgets/login#javascript-api

export type TgOAuthData = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

type TelegramLogin = {
  auth: (
    opts: { bot_id: number; request_access?: 'write' | boolean; lang?: string },
    callback: (data: TgOAuthData | false) => void,
  ) => void;
};

// Read window.Telegram.Login via a local cast rather than `declare global`
// because TwaProvider.tsx already augments window.Telegram with `WebApp?`;
// re-declaring the same property here would collide on the shape merge.
type WindowWithTelegramLogin = Window & {
  Telegram?: { Login?: TelegramLogin };
};

export class TgOAuthError extends Error {
  constructor(message: string, public readonly code: 'no-bot-id' | 'no-widget' | 'cancelled') {
    super(message);
  }
}

const WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';
let widgetLoadPromise: Promise<void> | null = null;

// Inject the Telegram widget script the first time it's needed and cache the
// in-flight load promise so concurrent callers share one network request.
// On failure we clear the cache so a subsequent click can retry (network
// blips, transient CDN issues). Skipped entirely if a prior page-load
// already left `window.Telegram.Login` populated.
function loadTelegramWidget(): Promise<void> {
  if ((window as WindowWithTelegramLogin).Telegram?.Login) {
    return Promise.resolve();
  }
  if (widgetLoadPromise) return widgetLoadPromise;
  widgetLoadPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = WIDGET_SRC;
    s.onload = () => resolve();
    s.onerror = () => {
      widgetLoadPromise = null;
      reject(new TgOAuthError('Failed to load Telegram widget script', 'no-widget'));
    };
    document.head.appendChild(s);
  });
  return widgetLoadPromise;
}

/**
 * Trigger the Telegram Login Widget popup. Resolves with the verified data
 * on success; rejects with TgOAuthError on cancel / misconfiguration.
 *
 * The widget script is lazy-loaded on first call so that pages which never
 * touch the TG flow (home, game, mine, ...) don't pay for the network
 * request.
 */
export async function launchTelegramOAuth(): Promise<TgOAuthData> {
  const botIdRaw = import.meta.env.VITE_TELEGRAM_BOT_ID;
  const botId = Number(botIdRaw);
  if (!Number.isFinite(botId) || botId <= 0) {
    throw new TgOAuthError('VITE_TELEGRAM_BOT_ID is not set', 'no-bot-id');
  }

  await loadTelegramWidget();

  const login = (window as WindowWithTelegramLogin).Telegram?.Login;
  if (!login) {
    throw new TgOAuthError('Telegram widget script not loaded', 'no-widget');
  }

  return new Promise<TgOAuthData>((resolve, reject) => {
    login.auth({ bot_id: botId, request_access: 'write' }, (data: TgOAuthData | false) => {
      if (!data) {
        reject(new TgOAuthError('User cancelled Telegram login', 'cancelled'));
        return;
      }
      resolve(data);
    });
  });
}

/**
 * Build the `data_check_string` the backend HMACs.
 * Per https://core.telegram.org/widgets/login#checking-authorization:
 *   - omit `hash`
 *   - sort the remaining keys alphabetically
 *   - join "key=value" pairs with "\n"
 */
export function buildDataCheckString(d: TgOAuthData): string {
  const pairs: Array<[string, string]> = [
    ['auth_date', String(d.auth_date)],
    ['first_name', d.first_name],
    ['id', String(d.id)],
  ];
  if (d.last_name) pairs.push(['last_name', d.last_name]);
  if (d.username) pairs.push(['username', d.username]);
  if (d.photo_url) pairs.push(['photo_url', d.photo_url]);
  pairs.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return pairs.map(([k, v]) => `${k}=${v}`).join('\n');
}
